// ProposalReview — the author's review queue for gated AGENT edits, pure.
//
// An agent edit lands as a PENDING proposal (it is NOT applied to content
// until the author approves — the asymmetry vs. an immediate manual edit).
// This surface lists the pending proposals for the material and lets the
// author ACCEPT (apply the patch) or REJECT (discard) each — distinct
// intents, distinct buttons, never one flag-swapping control.
//
// REJECT is a no-undo discard, so it is GUARDED: the first click arms a
// per-card confirm ("Discard this suggestion?"); only the confirm fires
// `onReject`. ACCEPT stays one-click (applying a proposal is reversible by
// re-asking the agent; a true undo-of-approve is the conversation rewind
// feature, out of scope here). Visually the two are NOT look-alikes —
// ACCEPT spends the accent (primary), REJECT is a quiet destructive control.
//
// Each card shows the agent's one-line rationale, WHERE the change lands
// (the element path), and WHAT it proposes (the patch: new text, or a full
// attrs replacement). The two actions report UP by proposal id; the host
// hits `/commands/acceptProposal | rejectProposal` and, on accept, reloads
// the now-changed material. Pure/props-driven: data in, intents out, no I/O.

import { exhaustSwitchCase } from "@bilda/utils";
import { Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { Button } from "../../Button/Button.js";
import { EmptyState } from "../../EmptyState/EmptyState.js";
import { Text } from "../../Text/Text.js";
import { describeAttrChange, describeElementPath } from "./humanize.js";
import "./ProposalReview.css";

// The proposed change — the SAME ElementPatch a manual edit uses. A sum,
// not a merge-with-mode flag: text and attrs are distinct proposals.
export type ProposalPatch =
    | { readonly type: "set_element_text"; readonly text: string }
    | {
          readonly type: "set_element_attrs";
          readonly attrs: Readonly<Record<string, unknown>>;
      };

// One pending proposal, as the review surface needs it.
export type LoadedProposal = {
    readonly proposalId: string;
    readonly path: readonly string[];
    readonly patch: ProposalPatch;
    readonly rationale: string;
};

export type ProposalReviewProps = {
    readonly proposals: readonly LoadedProposal[];
    readonly onAccept: (proposalId: string) => void;
    readonly onReject: (proposalId: string) => void;
    // Names the region for AT (caller-owned / localised).
    readonly ariaLabel: string;
    // True while a decision on `pendingId` is in flight — disables that
    // card's buttons so a double-click can't double-submit.
    readonly pendingId?: string;
    readonly className?: string;
};

// The location line — WHERE the change lands, in plain language. The raw
// id-path ("q1 › q1c1") means nothing to a subject-matter expert, so we phrase
// the element it names (which question, which option) instead. Off-convention
// ids degrade to a generic phrase — never the raw id.
const ElementLocation = ({ path }: { path: readonly string[] }): ReactNode => {
    const { t } = useLingui();
    const ref = describeElementPath(path);
    const label = ((): string => {
        switch (ref.type) {
            case "question":
                return t`question ${ref.n}`;
            case "stem":
                return t`the question stem in question ${ref.q}`;
            case "option":
                return t`option ${ref.label} in question ${ref.q}`;
            case "unknown":
                return t`this part of the material`;
            default:
                return exhaustSwitchCase(ref);
        }
    })();
    return (
        <Text size="sm" tone="dim">
            {t`In ${label}`}
        </Text>
    );
};

// Render the attrs change as a plain sentence — never a literal JSON blob.
const AttrChangeLine = ({
    attrs,
}: {
    attrs: Readonly<Record<string, unknown>>;
}): ReactNode => {
    const { t } = useLingui();
    const change = describeAttrChange(attrs);
    const sentence = ((): string => {
        switch (change.type) {
            case "mark_correct":
                return t`Mark as the correct answer`;
            case "unmark_correct":
                return t`Remove the correct-answer mark`;
            case "generic":
                return t`Adjust this part of the material`;
            default:
                return exhaustSwitchCase(change);
        }
    })();
    return (
        <p className="bilda-proposal-review__patch">
            <Text size="sm">{sentence}</Text>
        </p>
    );
};

// Render the patch as a short, human line — the "what will change".
const PatchSummary = ({ patch }: { patch: ProposalPatch }): ReactNode => {
    const { t } = useLingui();
    if (patch.type === "set_element_text") {
        return (
            <p className="bilda-proposal-review__patch">
                <Text size="sm" tone="dim">
                    {t`New text`}
                </Text>{" "}
                <Text size="sm">{patch.text}</Text>
            </p>
        );
    }
    return <AttrChangeLine attrs={patch.attrs} />;
};

// One review card. Owns only ephemeral UI state — whether its Reject is
// armed — so the no-undo discard needs a second, consequence-named click.
// The `onReject`/`onAccept` contract is untouched; the guard is local.
const ProposalCard = ({
    proposal,
    onAccept,
    onReject,
    busy,
}: {
    readonly proposal: LoadedProposal;
    readonly onAccept: (proposalId: string) => void;
    readonly onReject: (proposalId: string) => void;
    readonly busy: boolean;
}): ReactNode => {
    const { t } = useLingui();
    const [confirmingReject, setConfirmingReject] = useState(false);

    return (
        <article className="bilda-proposal-review__card">
            <Text weight="medium">{proposal.rationale}</Text>
            <ElementLocation path={proposal.path} />
            <PatchSummary patch={proposal.patch} />
            {confirmingReject ? (
                // biome-ignore lint/a11y/useSemanticElements: role="group" + aria-label on a styled div is intentional; <fieldset> brings default chrome the design system doesn't want. Semantic upgrade is a tracked a11y follow-up.
                <div
                    className="bilda-proposal-review__confirm"
                    role="group"
                    aria-label={t`Confirm discarding this suggestion`}
                >
                    <Text size="sm">{t`Discard this suggestion?`}</Text>
                    <div className="bilda-proposal-review__actions">
                        <Button
                            variant="ghost"
                            size="sm"
                            disabled={busy}
                            onClick={() => setConfirmingReject(false)}
                        >
                            <Trans>Keep it</Trans>
                        </Button>
                        <Button
                            variant="danger"
                            size="sm"
                            disabled={busy}
                            onClick={() => onReject(proposal.proposalId)}
                        >
                            <Trans>Discard</Trans>
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="bilda-proposal-review__actions">
                    <Button
                        variant="danger"
                        size="sm"
                        disabled={busy}
                        onClick={() => setConfirmingReject(true)}
                    >
                        <Trans>Reject</Trans>
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        disabled={busy}
                        onClick={() => onAccept(proposal.proposalId)}
                    >
                        <Trans>Approve</Trans>
                    </Button>
                </div>
            )}
        </article>
    );
};

export const ProposalReview = ({
    proposals,
    onAccept,
    onReject,
    ariaLabel,
    pendingId,
    className,
}: ProposalReviewProps): ReactNode => {
    const { t } = useLingui();
    const root = `bilda-proposal-review${className ? ` ${className}` : ""}`;

    if (proposals.length === 0) {
        return (
            <div className={root}>
                <EmptyState
                    title={t`No changes to review`}
                    body={t`When the agent proposes an edit, it waits here for your approval before it changes the material.`}
                />
            </div>
        );
    }

    return (
        <section aria-label={ariaLabel} className={root}>
            {proposals.map((proposal) => (
                <ProposalCard
                    key={proposal.proposalId}
                    proposal={proposal}
                    onAccept={onAccept}
                    onReject={onReject}
                    busy={pendingId === proposal.proposalId}
                />
            ))}
        </section>
    );
};
